import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeft, ArrowRight, Phone, UserPlus, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { errorMessage } from '../../api/client';
import * as refApi from '../../api/referrals.api';
import * as quizApi from '../../api/quiz.api';
import { BRAND_NAME } from '../../utils/brand';
import PasswordChecklist from '../../components/PasswordChecklist';
import {
  isStrongPassword,
  PASSWORD_HELPER,
  PASSWORD_WEAK_MSG,
  PASSWORD_MISMATCH_MSG,
} from '../../utils/passwordPolicy';
import { trackOtpVerified } from '../../utils/analytics';

// Where we stash a quiz payload that the landing page forwarded via
// ?intake=BASE64. Carried across the OTP step + survives page reload.
const INTAKE_STORAGE_KEY = 'mdai_pending_intake_v1';

// URL-safe base64 → JSON object. Returns null on any decode error so a
// tampered/garbled param is silently ignored.
function decodeIntake(raw) {
  if (!raw || typeof raw !== 'string') return null;
  try {
    const padded = raw.replace(/-/g, '+').replace(/_/g, '/');
    const pad = padded.length % 4 ? padded + '='.repeat(4 - (padded.length % 4)) : padded;
    const json = decodeURIComponent(escape(window.atob(pad)));
    const obj = JSON.parse(json);
    return obj && typeof obj === 'object' ? obj : null;
  } catch {
    return null;
  }
}

const MIN_LOCAL_DIGITS = 9;
const MAX_LOCAL_DIGITS = 10;

// USER sign-up wizard — phone → OTP + name + password → account created.
// Uses the new /auth/sign-up endpoint (verifies OTP + sets password in one
// server call so we never have a "phone verified but no password" limbo).
function Register() {
  const { sendOtp, signUpUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [step, setStep] = useState('phone'); // 'phone' | 'details'
  const [localDigits, setLocalDigits] = useState('');
  const [otp, setOtp] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [referralCode, setReferralCode] = useState(params.get('ref') || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const otpRef = useRef(null);

  useEffect(() => {
    const ref = params.get('ref');
    if (ref) refApi.trackReferralVisit(ref).catch(() => {});
  }, [params]);

  useEffect(() => {
    const raw = params.get('intake');
    if (!raw) return;
    const payload = decodeIntake(raw);
    if (payload) {
      try { sessionStorage.setItem(INTAKE_STORAGE_KEY, JSON.stringify(payload)); } catch { /* ignore */ }
    }
  }, [params]);

  useEffect(() => {
    if (step === 'details' && otpRef.current) otpRef.current.focus();
  }, [step]);

  function handleDigits(e) {
    let v = e.target.value.replace(/\D+/g, '');
    if (v.startsWith('0')) v = v.slice(1);
    setLocalDigits(v.slice(0, MAX_LOCAL_DIGITS));
  }

  function fullPhone() {
    return `+60${localDigits}`;
  }

  // Step 1: validate phone + password up front, then send OTP. Doing this
  // together (vs. the old phone-only step) means the customer fills the
  // whole form once and only enters the OTP on step 2 — the "complement
  // phone + password + OTP" flow the client asked for on 2026-08-03.
  async function onSendOtp(e) {
    e.preventDefault();
    setErr('');
    if (!name.trim()) {
      setErr('Please enter your full name.');
      return;
    }
    if (localDigits.length < MIN_LOCAL_DIGITS) {
      setErr(`Enter a valid Malaysian mobile number (${MIN_LOCAL_DIGITS}–${MAX_LOCAL_DIGITS} digits after +60).`);
      return;
    }
    if (!isStrongPassword(password)) {
      setErr(PASSWORD_WEAK_MSG);
      return;
    }
    if (password !== confirmPassword) {
      setErr(PASSWORD_MISMATCH_MSG);
      return;
    }
    setBusy(true);
    try {
      await sendOtp(fullPhone(), { purpose: 'signup' });
      toast.success(`OTP sent to ${fullPhone()}`);
      setStep('details');
    } catch (e1) {
      setErr(errorMessage(e1));
    } finally {
      setBusy(false);
    }
  }

  async function onSignUp(e) {
    e.preventDefault();
    setErr('');
    if (otp.length < 4) {
      setErr('Enter the 4-digit OTP we sent to your phone.');
      return;
    }
    setBusy(true);
    try {
      await signUpUser({
        phone: fullPhone(),
        otp,
        password,
        name: name.trim() || undefined,
        referralCode: referralCode.trim() || undefined,
      });
      toast.success('Account created');
      // GA4 conversion event — signup OTP verified successfully.
      trackOtpVerified({ purpose: 'signup' });

      // Auto-submit any pending quiz payload (from the landing intake) now
      // that we have a JWT. Failure is non-fatal — the user can retake.
      let intakeSubmitted = false;
      try {
        const raw = sessionStorage.getItem(INTAKE_STORAGE_KEY);
        if (raw) {
          const payload = JSON.parse(raw);
          await quizApi.submitQuiz(payload);
          sessionStorage.removeItem(INTAKE_STORAGE_KEY);
          intakeSubmitted = true;
        }
      } catch { /* ignore */ }

      // Redirect priority (highest → lowest):
      //   1. `?from=…` — user came from a specific deep link (e.g. clicked
      //      "Get my link" on the landing referral banner → /referrals).
      //   2. `/packages` — they arrived with a landing intake to submit.
      //   3. `/profile` — brand-new signup, complete their details next.
      const fromParam = params.get('from');
      const dest = fromParam
        ? fromParam
        : (intakeSubmitted ? '/packages' : '/profile');
      navigate(dest, { replace: true });
    } catch (e1) {
      setErr(errorMessage(e1));
    } finally {
      setBusy(false);
    }
  }

  async function onResend() {
    setErr('');
    setBusy(true);
    try {
      await sendOtp(fullPhone(), { purpose: 'signup' });
      toast.success('OTP resent');
    } catch (e1) {
      setErr(errorMessage(e1));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-card">
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 'var(--s-4)' }}>
        <img src="/fullLogo.png" alt={BRAND_NAME} style={{ height: 48, width: 'auto' }} draggable="false" />
      </div>

      {step === 'phone' && (
        <>
          <h2>Sign up</h2>
          <form onSubmit={onSendOtp}>
            <div className="field">
              <label>Mobile number</label>
              <div
                className="row"
                style={{
                  border: '1px solid var(--border)', borderRadius: 8,
                  padding: '8px 12px', background: 'var(--surface)', gap: 8,
                }}
              >
                <span style={{ color: 'var(--ink-600)', fontWeight: 600 }}>+60</span>
                <input
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  autoComplete="tel"
                  value={localDigits}
                  onChange={handleDigits}
                  placeholder="123456789"
                  style={{ flex: 1, border: 0, outline: 'none', background: 'transparent', fontSize: 16 }}
                  required
                />
              </div>
            </div>

            <div className="field">
              <label>Your full name</label>
              <input
                className="input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Aisha binti Rahman"
                autoComplete="name"
                required
                minLength={2}
              />
            </div>

            <div className="field">
              <label>Password</label>
              <div
                className="row"
                style={{
                  border: '1px solid var(--border)', borderRadius: 8,
                  padding: '8px 12px', background: 'var(--surface)', gap: 8,
                }}
              >
                <input
                  type={showPw ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  style={{ flex: 1, border: 0, outline: 'none', background: 'transparent', fontSize: 16 }}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? 'Hide password' : 'Show password'}
                  style={{ background: 'transparent', border: 0, cursor: 'pointer', color: 'var(--ink-500)' }}
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <div className="muted text-xs" style={{ marginTop: 6 }}>{PASSWORD_HELPER}</div>
              <PasswordChecklist password={password} />
            </div>

            <div className="field">
              <label>Confirm Password</label>
              <input
                className="input"
                type={showPw ? 'text' : 'password'}
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter your password"
                required
              />
              {confirmPassword && confirmPassword !== password && (
                <div className="muted text-xs" style={{ marginTop: 6, color: 'var(--danger, #dc2626)' }}>
                  {PASSWORD_MISMATCH_MSG}
                </div>
              )}
            </div>

            <div className="field">
              <label>Referral code <span className="muted text-xs">(optional)</span></label>
              <input
                className="input"
                type="text"
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value)}
                placeholder="e.g. AISHA-A1B2C3"
                autoComplete="off"
              />
            </div>

            {err && <div className="notice warning" style={{ marginBottom: 'var(--s-4)' }}>{err}</div>}

            <button type="submit" className="btn btn-primary btn-block" disabled={busy}>
              {busy ? <span className="spinner" /> : <Phone size={16} />}
              {busy ? 'Sending…' : 'Send OTP'}
            </button>
          </form>

          <div className="divider" />
          <div className="text-sm muted" style={{ textAlign: 'center' }}>
            Already registered? <Link to="/login">Sign in</Link>
          </div>
        </>
      )}

      {step === 'details' && (
        <>
          <h2>Verify your phone</h2>
          <div className="auth-sub">
            We sent a 4-digit code to <strong>{fullPhone()}</strong>. Enter it below to finish creating your account.
          </div>

          <form onSubmit={onSignUp}>
            <div className="field">
              <label>OTP</label>
              <input
                ref={otpRef}
                className="input"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                autoComplete="one-time-code"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D+/g, ''))}
                placeholder="1111"
                style={{ letterSpacing: 4, fontSize: 18, textAlign: 'center' }}
                required
              />
            </div>

            {err && <div className="notice warning" style={{ marginBottom: 'var(--s-4)' }}>{err}</div>}

            <button type="submit" className="btn btn-primary btn-block" disabled={busy}>
              {busy ? <span className="spinner" /> : <UserPlus size={16} />}
              {busy ? 'Creating account…' : 'Create account'}
            </button>
          </form>

          <div className="row between" style={{ marginTop: 'var(--s-4)' }}>
            <button
              type="button"
              onClick={() => { setStep('phone'); setOtp(''); setErr(''); }}
              style={{ background: 'transparent', border: 0, cursor: 'pointer', color: 'var(--brand-700)' }}
              className="text-sm"
            >
              <ArrowLeft size={14} /> Edit details
            </button>
            <button
              type="button"
              onClick={onResend}
              disabled={busy}
              style={{ background: 'transparent', border: 0, cursor: 'pointer', color: 'var(--brand-700)' }}
              className="text-sm"
            >
              Resend code <ArrowRight size={14} />
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default Register;
